/**
 * Configuracoes do jogo, com presets de qualidade e persistencia em localStorage.
 *
 * Todo sistema que tem custo grafico ajustavel DEVE ler daqui em vez de fixar
 * numeros no codigo, e DEVE reagir a settings.onChange(...) quando fizer sentido.
 */

const PRESETS = {
  low: {
    label: 'Baixo',
    resolutionScale: 0.75,
    shadowsEnabled: true,
    shadowMapSize: 1024,
    shadowCascades: 2,
    shadowDistance: 60,
    ssao: false,
    bloom: true,
    motionBlur: false,
    depthOfField: false,
    ssr: false,
    antialias: 'fxaa',
    anisotropy: 4,
    particleScale: 0.4,
    maxDecals: 48,
    volumetrics: false
  },
  medium: {
    label: 'Medio',
    resolutionScale: 1.0,
    shadowsEnabled: true,
    shadowMapSize: 2048,
    shadowCascades: 3,
    shadowDistance: 90,
    ssao: true,
    bloom: true,
    motionBlur: false,
    depthOfField: false,
    ssr: false,
    antialias: 'smaa',
    anisotropy: 8,
    particleScale: 0.7,
    maxDecals: 96,
    volumetrics: false
  },
  high: {
    label: 'Alto',
    resolutionScale: 1.0,
    shadowsEnabled: true,
    shadowMapSize: 2048,
    shadowCascades: 4,
    shadowDistance: 120,
    ssao: true,
    bloom: true,
    motionBlur: true,
    depthOfField: true,
    ssr: false,
    antialias: 'smaa',
    anisotropy: 16,
    particleScale: 1.0,
    maxDecals: 160,
    volumetrics: true
  },
  ultra: {
    label: 'Ultra',
    resolutionScale: 1.0,
    shadowsEnabled: true,
    shadowMapSize: 4096,
    shadowCascades: 4,
    shadowDistance: 160,
    ssao: true,
    bloom: true,
    motionBlur: true,
    depthOfField: true,
    ssr: true,
    antialias: 'smaa',
    anisotropy: 16,
    particleScale: 1.4,
    maxDecals: 256,
    volumetrics: true
  }
};

const USER_DEFAULTS = {
  // `quality` aqui e so o ultimo recurso: quem decide na PRIMEIRA execucao e o
  // detectarQualidade() abaixo, olhando o aparelho. Dar 'high' a todo mundo -
  // com motion blur, profundidade de campo, SSAO e 4 cascatas de sombra - era o
  // que fazia o jogo nascer pesado em maquina modesta e em celular, e frame caro
  // vira fila de apresentacao, que o jogador sente como "o mouse demora".
  quality: 'medium',
  // 110 e o campo de visao pedido. FPS competitivo joga entre 100 e 120; 80 e
  // valor de jogo de tiro em terceira pessoa e da sensacao de tunel.
  fov: 110,
  sensitivity: 0.0022,
  adsSensitivityScale: 0.65,
  invertY: false,
  masterVolume: 0.8,
  sfxVolume: 1.0,
  musicVolume: 0.5,
  showFps: false,
  crosshairEnabled: true,
  // Mapa escolhido. 'setor7' e o quarteirao original; os demais vem de
  // src/world/maps/. Lido pelo Level no boot.
  mapId: 'setor7',
  cameraBob: 1.0,
  screenShake: 1.0
};

const STORAGE_KEY = 'blackout.settings.v1';

/**
 * Preset da PRIMEIRA execucao, escolhido pelo aparelho.
 *
 * Decide por capacidade, nunca por user agent (UA mente ha anos). Os sinais:
 *   - ponteiro grosso + tela pequena  -> celular: GPU movel, sempre 'low'
 *   - poucos nucleos ou pouca memoria -> maquina modesta: 'medium'
 *   - o resto                          -> 'medium', e quem quiser sobe no menu
 *
 * Por que nem os fortes ganham 'high' de saida: 'high' liga profundidade de
 * campo e motion blur, que sao caros e sao GOSTO. Melhor o jogo nascer
 * respondendo e o jogador subir a qualidade do que nascer bonito e travado.
 */
function detectarQualidade() {
  try {
    const toque = (navigator.maxTouchPoints || 0) > 0;
    const grosso = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const largura = Math.min(window.screen?.width || 9999, window.innerWidth || 9999);
    if (toque && (grosso || largura <= 900)) return 'low';

    const nucleos = navigator.hardwareConcurrency || 4;
    const memoria = navigator.deviceMemory || 8;   // GB, so no Chromium
    if (nucleos <= 4 || memoria <= 4) return 'low';
    return 'medium';
  } catch {
    return 'medium';
  }
}

export class Settings {
  constructor() {
    this._listeners = new Set();
    const stored = this._load();
    const primeiraVez = !stored || stored.quality === undefined;
    Object.assign(this, USER_DEFAULTS, stored);
    // Escolha ja gravada pelo jogador SEMPRE vence a deteccao.
    if (primeiraVez) this.quality = detectarQualidade();
    this._applyPreset(this.quality);
    if (primeiraVez) this.save();
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  save() {
    try {
      const out = {};
      for (const key of Object.keys(USER_DEFAULTS)) out[key] = this[key];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(out));
    } catch {
      /* localStorage indisponivel - seguir sem persistir */
    }
  }

  _applyPreset(name) {
    const preset = PRESETS[name] || PRESETS.high;
    Object.assign(this, preset);
    this.quality = PRESETS[name] ? name : 'high';
  }

  get presets() {
    return PRESETS;
  }

  setQuality(name) {
    if (!PRESETS[name]) return;
    this._applyPreset(name);
    this.save();
    this._emit('quality');
  }

  set(key, value) {
    if (this[key] === value) return;
    this[key] = value;
    this.save();
    this._emit(key);
  }

  /** @param {(key: string, settings: Settings) => void} fn */
  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit(key) {
    for (const fn of this._listeners) {
      try {
        fn(key, this);
      } catch (err) {
        console.error('[Settings] ouvinte falhou:', err);
      }
    }
  }
}
